import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  renameRole,
  renameRoleForEnterprise,
  UserKeys,
} from '../../../api/users-api';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import SharedDialog from '../../../shared/shared-dialog';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { ModuleList } from '../../../shared/analytics/module-list';
import * as analytics from '../../../shared/analytics/analytics.json';
import { UserRole } from '../../../../types';
import { AdministrationRoles } from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { useUserSettings } from '../../../utils/use-user-settings';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
interface RoleRenameDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRole: UserRole;
  setSelectedRole: Dispatch<SetStateAction<UserRole | null>>;
  changeLogRoles: string[];
  roleNames: string[];
}

export default function RoleRenameDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedRole,
  setSelectedRole,
  roleNames,
}: RoleRenameDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState(selectedRole.roleName);
  const [newRoleNameError, setNewRoleNameError] = useState(false);
  const [newRoleNameExistsError, setNewRoleNameExistsError] = useState(false);
  const { setSnackbar } = useStore();
  const { features, currentTier } = useUserSettings();

  // Check if we should use enterprise endpoint
  const shouldUseEnterpriseEndpoint =
    currentTier === 'enterprise' &&
    isFeatureEnabled('indexUsersReact', features);

  // Standard mutation - synchronous rename
  const standardMutation = useMutation({
    mutationFn: () =>
      renameRole(selectedRole.roleName, newName, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Renamed('Role'),
      });

      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) {
          return;
        } else {
          const filteredList = oldData.filter(
            (currentRole) => currentRole.roleName !== selectedRole.roleName
          );
          return [...filteredList, data];
        }
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRoles],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRole],
      });
      setChangeLogMessage('');
      setSelectedRole(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Renaming('this role'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.role_manager.rename_role, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
        roleType: selectedRole.roleType,
      });
      setOpenChangeLogDialog(false);
    },
  });

  // Enterprise mutation - async rename (kicks off task)
  const enterpriseMutation = useMutation({
    mutationFn: () =>
      renameRoleForEnterprise(selectedRole.roleName, newName, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'Role rename task has been scheduled and will be processed shortly.',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.Roles],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRoles],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRole],
      });

      setChangeLogMessage('');
      setSelectedRole(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Renaming('this role'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.role_manager.rename_role, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
        roleType: selectedRole.roleType,
        isAsync: true,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate, isPending } = shouldUseEnterpriseEndpoint
    ? enterpriseMutation
    : standardMutation;

  function handleSubmit() {
    if (newName.trim().length === 0) {
      setNewRoleNameError(true);
    } else if (roleNamesLowerCase.includes(newName.toLowerCase().trim())) {
      setNewRoleNameExistsError(true);
    } else {
      setNewRoleNameExistsError(false);
      setNewRoleNameError(false);
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          AdministrationRoles.RenameRole.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  const roleNamesLowerCase = roleNames.map((name) => name.toLowerCase());

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedRole(null);
        handleClose();
      }}
      title="Rename Role"
      dialogContent={
        <Box>
          <TextField
            fullWidth
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={newRoleNameError || newRoleNameExistsError}
            helperText={
              newRoleNameError
                ? 'Please provide a new role name.'
                : newRoleNameExistsError
                ? 'Role name is currently in use. Please provide a different name.'
                : ' '
            }
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Rename Role',
        action: handleSubmit,
        loading: isPending,
        disabled: selectedRole.roleName === newName,
      }}
    />
  );
}
